import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaCloudArrowUp, FaHardDrive, FaSpinner, FaTrash } from 'react-icons/fa6'
import Button from '@renderer/components/Button'
import { getDiskCleanupApi } from '@renderer/lib/runtime'
import type {
  DiskCleanupPlanSummary,
  DiskCleanupRunResult,
  DiskCleanupStatus
} from '../../../../shared/diskCleanup'

const GB = 1024 * 1024 * 1024
/** Free disk barely moves between sessions, and each read spawns a process on Windows. */
const STATUS_POLL_MS = 10 * 60 * 1000

export const formatGB = (bytes: number | null | undefined): string =>
  bytes == null ? '–' : `${(bytes / GB).toFixed(bytes < 10 * GB ? 1 : 0)} GB`

/**
 * Free disk on this booth, polled while `enabled`. Null on shells without the
 * cleanup IPC (the renderer is web-served and reaches older shells too).
 */
export function useDiskCleanupStatus(enabled: boolean): {
  status: DiskCleanupStatus | null
  refresh: () => void
} {
  const [status, setStatus] = useState<DiskCleanupStatus | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const api = getDiskCleanupApi()
    if (!enabled || !api) return
    let cancelled = false
    const read = (): void => {
      api
        .getStatus()
        .then((next) => {
          if (!cancelled) setStatus(next)
        })
        .catch((error) => console.warn('[DISK-CLEANUP] status failed:', error))
    }
    read()
    const timer = setInterval(read, STATUS_POLL_MS)
    return (): void => {
      cancelled = true
      clearInterval(timer)
    }
  }, [enabled, tick])

  return { status, refresh: () => setTick((n) => n + 1) }
}

interface DiskCleanupModalProps {
  isOpen: boolean
  onClose: () => void
  /** Called after a run so callers can re-read the free space. */
  onCleaned?: () => void
}

/**
 * Operator-only: shows what an approved cleanup would free, and deletes only
 * after the confirm button. Main re-checks every session with the server first.
 */
const DiskCleanupModal: React.FC<DiskCleanupModalProps> = ({ isOpen, onClose, onCleaned }) => {
  const { t, i18n } = useTranslation()
  const [plan, setPlan] = useState<DiskCleanupPlanSummary | null>(null)
  const [result, setResult] = useState<DiskCleanupRunResult | null>(null)
  const [phase, setPhase] = useState<'planning' | 'ready' | 'running' | 'done' | 'error'>('planning')

  const makePlan = (): void => {
    const api = getDiskCleanupApi()
    if (!api) return
    setPhase('planning')
    setResult(null)
    api
      .plan()
      .then((next) => {
        setPlan(next)
        setPhase('ready')
      })
      .catch((error) => {
        console.error('[DISK-CLEANUP] plan failed:', error)
        setPhase('error')
      })
  }

  useEffect(() => {
    if (isOpen) makePlan()
  }, [isOpen])

  if (!isOpen || !getDiskCleanupApi()) return null

  const run = async (): Promise<void> => {
    const api = getDiskCleanupApi()
    if (!api || !plan) return
    setPhase('running')
    try {
      const outcome = await api.run(plan.planId)
      if (outcome.stalePlan) {
        makePlan()
        return
      }
      setResult(outcome)
      setPhase(outcome.success ? 'done' : 'error')
      onCleaned?.()
    } catch (error) {
      console.error('[DISK-CLEANUP] run failed:', error)
      setPhase('error')
    }
  }

  const day = (ms: number | null): string =>
    ms == null ? '' : new Date(ms).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })
  const busy = phase === 'planning' || phase === 'running'
  const nothingToDo = phase === 'ready' && plan !== null && plan.bytes === 0

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-carbon">
          <FaHardDrive className="text-gray-500" />
          {t('diskCleanup.title', 'Storage')}
          <span className="ml-auto text-sm font-normal text-gray-500">
            {t('diskCleanup.free', '{{size}} free', {
              size: formatGB(result?.freeBytes ?? plan?.freeBytes)
            })}
          </span>
        </h3>

        {busy && (
          <p className="flex items-center gap-2 py-4 text-sm text-gray-600">
            <FaSpinner className="animate-spin" />
            {phase === 'planning'
              ? t('diskCleanup.scanning', 'Checking files…')
              : t('diskCleanup.running', 'Cleaning…')}
          </p>
        )}

        {phase === 'ready' && plan && !nothingToDo && (
          <div className="space-y-1 py-2">
            <p className="text-3xl font-bold text-carbon">{formatGB(plan.bytes)}</p>
            <p className="text-sm text-gray-600">
              {t('diskCleanup.sessions', '{{count}} sessions', { count: plan.sessionCount })} ·{' '}
              {day(plan.oldestMs)} – {day(plan.newestMs)}
            </p>
            {plan.projectedFreeBytes != null && (
              <p className="text-sm text-gray-600">
                → {t('diskCleanup.free', '{{size}} free', { size: formatGB(plan.projectedFreeBytes) })}
              </p>
            )}
          </div>
        )}

        {nothingToDo && (
          <p className="py-4 text-sm text-gray-600">
            {t('diskCleanup.nothing', 'Nothing old enough to delete.')}
          </p>
        )}

        {phase === 'ready' && plan && plan.protectedSessions > 0 && (
          <p className="mt-2 flex items-center gap-2 text-xs text-gray-500">
            <FaCloudArrowUp className="shrink-0" />
            {t('diskCleanup.protected', '{{count}} not uploaded yet, kept', {
              count: plan.protectedSessions
            })}
          </p>
        )}

        {phase === 'done' && result && (
          <div className="space-y-1 py-2">
            <p className="flex items-center gap-2 text-3xl font-bold text-carbon">
              <FaCheck className="text-xl text-green-600" />
              {formatGB(result.freedBytes)}
            </p>
            <p className="text-sm text-gray-600">
              {t('diskCleanup.deleted', '{{count}} sessions deleted', { count: result.deletedSessions })}
            </p>
            {result.skippedUnverified > 0 && (
              <p className="text-xs text-gray-500">
                {t('diskCleanup.skippedOffline', '{{count}} kept: server unreachable', {
                  count: result.skippedUnverified
                })}
              </p>
            )}
            {result.skippedNotUploaded > 0 && (
              <p className="text-xs text-gray-500">
                {t('diskCleanup.skippedNotUploaded', '{{count}} kept: not on the server yet', {
                  count: result.skippedNotUploaded
                })}
              </p>
            )}
          </div>
        )}

        {phase === 'error' && (
          <p className="py-4 text-sm text-red-600">{t('diskCleanup.failed', 'Cleanup failed. Try again.')}</p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button
            text={phase === 'done' ? t('common.close', 'Close') : t('common.cancel', 'Cancel')}
            variant="secondary"
            onClick={onClose}
            disabled={phase === 'running'}
            className="!px-5 !py-2 !text-base"
          />
          {(phase === 'ready' || phase === 'running') && !nothingToDo && (
            <Button
              text={
                <span className="flex items-center gap-2">
                  {phase === 'running' ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                  {t('diskCleanup.confirm', 'Clean up')}
                </span>
              }
              onClick={run}
              disabled={busy}
              className="!px-5 !py-2 !text-base"
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default DiskCleanupModal
